import { ReactElement, useContext } from 'react';
import { LocaleContext } from '../../context/LocaleContext';
import { MultiValuesNode } from '../../../types/chartDataTypes';
import { Container, Row } from './DataTable.styles';
import Link from '../Link/Link';
import Button from '../Button/Button';
import Header from '../Header/Header';
import { addWordBreaks } from '../../helper/strings';

export type DataLayerTableProps = {
  headline?: string;
  note?: string;
  data: MultiValuesNode;
  columnKeys: string[];
  firstColumnLabel?: string;
  columnLabels?: {[key: string]: string};
  /** if set to true the first column header can run into the empty name header */
  expandFirstHeader?: boolean;
  /** Can be used to render the table rows in a lower level than they really are */
  renderLevelShift?: number;
  /** Will use the specified level if the render level is smaller */
  minRenderLevel?: number;
  /** Will skip entries with a lower level */
  maxLevel?: number;
  markEntryWithName?: string;
  onLineHover?: (lineName: string) => void;
  entryClickTargetBuilder?: (entry: MultiValuesNode, column: string, level: number) => string | (() => void);
  className?: string;
};

const DataTable = ({
  headline,
  note,
  data,
  columnKeys,
  firstColumnLabel,
  columnLabels,
  expandFirstHeader,
  renderLevelShift = 0,
  minRenderLevel = 0,
  maxLevel = Number.MAX_VALUE,
  markEntryWithName,
  onLineHover,
  entryClickTargetBuilder,
  className,
}: DataLayerTableProps): ReactElement => {
  const { formatter } = useContext(LocaleContext);

  const columnCount = columnKeys.length + 1;

  const SpacerRow = ({ level }: { level: number }): ReactElement => (
    <tr className={`--spacer --s-level-${level}`}>
      <td colSpan={columnCount}>&nbsp;</td>
    </tr>
  );

  const renderRows = (entry: MultiValuesNode, level: number, noSpacerAfter = false): any[] => {
    if (maxLevel != null && maxLevel < level) {
      return [];
    }
    const renderLevel = level + renderLevelShift < minRenderLevel
      ? minRenderLevel : level + renderLevelShift;

    const result = entry.entries?.reduce((childResult, subEntry) => (
      childResult.concat(renderRows(subEntry, level + 1, noSpacerAfter || renderLevel <= 1))
    ), [] as any[]) || [] as any[];

    const entryName = entry.totalLabel || entry.name;
    if (entryName && Object.keys(entry.values || {}).length) {
      const isMarked = !!markEntryWithName && markEntryWithName === entryName;
      const addLine = result.length && renderLevel > 0;
      const addSpacerBefore = (renderLevel === 1 && addLine) || renderLevel === 0;

      const onMouseOverLine = (): void => {
        if (onLineHover) {
          onLineHover(entryName);
        }
      };
      const onMouseLeaveLine = (): void => {
        if (onLineHover) {
          onLineHover(null);
        }
      };

      if (addSpacerBefore) {
        result.push((
          <SpacerRow
            key={`spacer_${entry.name || entry.totalLabel}`}
            level={renderLevel}
          />
        ));
      }

      result.push((
        <Row
          key={entry.name || entry.totalLabel}
          className={`--level-${renderLevel} ${addLine ? '--line' : ''} ${isMarked ? '--marked' : ''}`}
          onMouseOver={onMouseOverLine}
          onMouseOut={onMouseLeaveLine}
        >
          <td>{addWordBreaks(entry.totalLabel || entry.name)}</td>
          {columnKeys.map((key) => {
            const value = (entry.values || {})[key];
            const dataString = value == null ? '' : formatter.formatNumber(value);

            const clickTarget = entryClickTargetBuilder
              ? entryClickTargetBuilder(entry, key, level) : undefined;
            let entryContent: string | ReactElement = dataString;
            if (typeof clickTarget === 'string' && clickTarget) {
              entryContent = (
                <Link href={clickTarget} target="notes" linkText={dataString} />
              );
            } else if (typeof clickTarget === 'function') {
              entryContent = (
                <Button type="button" mode="link" onClick={clickTarget} label={dataString} />
              );
            }

            return (
              // eslint-disable-next-line max-len
              // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-noninteractive-element-interactions
              <td key={`${key}_${entry.name || entry.totalLabel}`} className="--right">
                {entryContent}
              </td>
            );
          })}
        </Row>
      ));

      if (renderLevel === 1 && !noSpacerAfter) {
        result.push((
          <SpacerRow
            key={`spacer2_${entry.name || entry.totalLabel}`}
            level={renderLevel}
          />
        ));
      }
    }
    return result;
  };

  return (
    <Container className={className}>
      {!(!headline && !note) && (
        <Header headline={headline} note={note} />
      )}
      <table>
        <colgroup>
          <col className={`--first ${columnKeys.length === 1 ? '--long' : ''}`} />
          {columnKeys.map((key) => (
            <col key={key} className="--data" />
          ))}
        </colgroup>
        <thead>
          <tr>
            {!expandFirstHeader && (
              <th>{firstColumnLabel || <>&nbsp;</>}</th>
            )}
            {columnKeys.map((key, index) => {
              const label = columnLabels?.[key] || key;
              return (
                <th
                  key={key}
                  colSpan={expandFirstHeader && index === 0 ? 2 : undefined}
                  className="--right"
                >
                  {label}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          <SpacerRow level={null} />
          {renderRows(data, 0)}
        </tbody>
      </table>
    </Container>
  );
};

export default DataTable;
